/**
 * htmlToMjml — Convert legacy email HTML to MJML for GrapesJS editing.
 *
 * Two input shapes are handled:
 *
 * PATH A — Original table-based HTML (first-ever load of a DB template):
 *   <table width="100%">
 *     <tr><td>
 *       <table width="600">   ← email container
 *         <tr><td style="bg/padding"> → mj-section
 *       </table>
 *     </td></tr>
 *   </table>
 *
 * PATH B — MJML-compiled HTML (reload after first GrapesJS save):
 *   MJML compile output uses max-width:600px divs + .mj-column-per-100
 *   wrappers instead of table[width="600"].  Without special handling,
 *   the fallback would bundle the entire email into ONE mj-text — all
 *   inner <td> cells then become selectable "Table cell" components and
 *   the RTE never activates.  This path reverses the MJML compile back
 *   to mj-section / mj-column / mj-text structure.
 *
 * Structure of MJML-compiled output (per section):
 *   <div style="...max-width:600px;">          ← mj-section (bg from div)
 *     <table>
 *       <tbody><tr>
 *         <td style="direction:ltr;padding:…">  ← section padding
 *           <div class="mj-column-per-100 …">   ← mj-column
 *             <table><tbody>
 *               <tr>
 *                 <td align="…" style="padding:…"> ← one mj-text
 *                   <div style="font-family:…">    ← content wrapper
 *                     [actual content html]
 *                   </div>
 *                 </td>
 *               </tr>
 *             </tbody></table>
 *           </div>
 *         </td>
 *       </tr></tbody>
 *     </table>
 *   </div>
 */

// ─── Public API ──────────────────────────────────────────────────────────────

export function htmlToMjml(html: string): string {

  // Pre-process: section-level variables like {{mandatory_notice_section}}
  // appear between </tr> and <tr> — wrap them in proper rows so DOMParser
  // doesn't foster-parent them out of the table.
  const processed = protectTableTokens(html.replace(
    /(<\/tr>\s*)(\{\{\{?[\w_]+\}\}\}?)(\s*<tr)/gi,
    '$1<tr><td>$2</td></tr>$3',
  ));

  const doc = new DOMParser().parseFromString(processed, 'text/html');

  // Body background
  const bodyBg =
    getStyleProp(doc.querySelector('body'), 'background-color') ||
    getStyleProp(doc.querySelector('table'), 'background-color') ||
    '#f4f4f4';

  // ── PATH B: MJML-compiled HTML ──
  // Detected by the presence of .mj-column-per-100 or .mj-outlook-group-fix,
  // which are class names exclusively emitted by the MJML compiler.
  if (doc.querySelector('.mj-column-per-100, .mj-outlook-group-fix')) {
    return fromMjmlCompiled(doc, bodyBg);
  }

  // ── PATH A: Original table-based HTML ──
  const container = doc.querySelector('table[width="600"]') as HTMLTableElement | null;

  if (!container) {
    // Fallback: wrap entire body in one section
    return wrapMjml(bodyBg, mjSection({}, [doc.body.innerHTML]));
  }

  const sections: string[] = [];

  for (const row of Array.from(container.rows)) {
    const td = row.cells[0];
    if (!td) continue;

    const bg = getStyleProp(td, 'background-color');
    const padding = getStyleProp(td, 'padding') || '20px 30px';
    const radius = getStyleProp(td, 'border-radius');
    const align = getStyleProp(td, 'text-align');

    const blocks = extractBlocks(td, align);
    if (!blocks.length) continue;

    sections.push(mjSection({ bg, padding, radius }, blocks));
  }

  return wrapMjml(bodyBg, sections.join('\n'));
}

// ─── Placeholders inside table structure ─────────────────────────────────────

// A {{token}} between table rows (e.g. <tbody>{{{items_rows}}}</tbody>) gets moved out by the
// browser. Keep it as a comment while editing; restoreTableTokens puts it back on save/preview.
const TABLE_TOKEN_PREFIX = 'SZ_TPL:';
// Gap = only whitespace, HTML comments and tokens between two table structure tags.
// A comment body may not contain "-->", so each comment matches one way only
// (a lazy [\s\S]*? could stretch across comments and freeze on many in a row).
const TABLE_GAP_TOKENS =
  /(<\/?(?:table|tbody|thead|tfoot|tr)\b[^>]*>|<\/t[dh]\s*>)((?:\s*(?:<!--(?:(?!-->)[\s\S])*-->|\{\{\{?[^{}]+?\}?\}\}))+\s*)(?=<\/?(?:table|tbody|thead|tfoot|tr|td|th)\b)/gi;

export function protectTableTokens(html: string): string {
  if (!html || !html.includes('{{')) return html;
  return html.replace(TABLE_GAP_TOKENS, (match: string, tag: string, gap: string) => {
    if (!gap.includes('{{')) return match;
    // Only the tokens are wrapped; existing comments in the gap stay as they are.
    return tag + gap.replace(/(<!--[\s\S]*?-->)|\{\{\{?[^{}]+?\}?\}\}/g, (token: string, comment?: string) =>
      comment ? comment : `<!--${TABLE_TOKEN_PREFIX}${encodeURIComponent(token).replace(/-/g, '%2D')}-->`,
    );
  });
}

export function restoreTableTokens(html: string): string {
  if (!html || !html.includes(TABLE_TOKEN_PREFIX)) return html;
  return html.replace(new RegExp(`<!--${TABLE_TOKEN_PREFIX}([^>]*?)-->`, 'g'), (match: string, encoded: string) => {
    try { return decodeURIComponent(encoded); } catch { return match; }
  });
}

// ─── PATH B: MJML-compiled HTML reverse-parser ───────────────────────────────

/**
 * Re-construct MJML from MJML-compiled HTML.
 *
 * Sections = direct children of [aria-roledescription="email"] (or body)
 *            that carry max-width:600px.
 * Columns  = .mj-column-per-100 / .mj-outlook-group-fix inside each section.
 * mj-texts = each <tr><td><div style="font-family:…"> row inside the column table.
 */
function fromMjmlCompiled(doc: Document, bodyBg: string): string {
  // Use the MJML email root div if present, otherwise fall back to body.
  const emailRoot =
    (doc.querySelector('[aria-roledescription="email"]') as HTMLElement | null) ||
    doc.body;

  // Collect section-level divs: direct children that carry max-width:600px.
  const sectionDivs = Array.from(emailRoot.children).filter(
    (el): el is HTMLElement =>
      el instanceof HTMLElement &&
      /max-width\s*:\s*600px/i.test(el.getAttribute('style') || ''),
  );

  if (!sectionDivs.length) {
    // No recognisable section structure — fall back to single mj-text.
    return wrapMjml(bodyBg, mjSection({}, [`    <mj-text>${emailRoot.innerHTML}</mj-text>`]));
  }

  const sections: string[] = [];

  for (const sectionEl of sectionDivs) {
    const sectionStyle = sectionEl.getAttribute('style') || '';

    // MJML emits both `background:` and `background-color:` on section divs;
    // prefer explicit background-color.
    const bg = (
      (sectionStyle.match(/background-color\s*:\s*([^;]+)/i) || [])[1] || ''
    ).trim();

    // border-radius is written directly on the section <div> by MJML compiler.
    const radius = (
      (sectionStyle.match(/border-radius\s*:\s*([^;]+)/i) || [])[1] || ''
    ).trim();

    // Section padding lives on the td[style*="direction:ltr"] wrapper.
    const sectionTd = sectionEl.querySelector(
      'td[style*="direction:ltr"]',
    ) as HTMLTableCellElement | null;
    const padding = sectionTd
      ? getStyleProp(sectionTd, 'padding') || '20px 30px'
      : '20px 30px';

    // Column element (the 100%-width column wrapper MJML generates).
    const columnEl = (sectionTd ?? sectionEl).querySelector(
      '.mj-column-per-100, .mj-outlook-group-fix',
    ) as HTMLElement | null;

    if (!columnEl) {
      // Degenerate section: no column div found.
      const fallback = (sectionTd ?? sectionEl).innerHTML;
      sections.push(
        mjSection({ bg: bg || undefined, padding, radius: radius || undefined }, [`    <mj-text>${fallback}</mj-text>`]),
      );
      continue;
    }

    // The column's inner <table><tbody> holds one <tr> per original mj-text.
    const tbody = columnEl.querySelector('table > tbody') as HTMLTableSectionElement | null;
    if (!tbody) continue;

    // A card column (own background/border) becomes ONE text block holding the styled box,
    // like other card boxes, so it copies/moves the same way and keeps all of its rows.
    const card = columnCard(tbody);
    const blocks = card ? [card] : compiledRowsToBlocks(tbody);

    if (blocks.length) {
      sections.push(mjSection({ bg: bg || undefined, padding, radius: radius || undefined }, blocks));
    }
  }

  return wrapMjml(bodyBg, sections.join('\n'));
}

/** One <mj-text> per compiled row; the text wrapper's own style is kept as attributes. */
function compiledRowsToBlocks(tbody: HTMLTableSectionElement): string[] {
  const blocks: string[] = [];
  for (const tr of Array.from(tbody.rows)) {
    const td = tr.cells[0];
    if (!td) continue;

    // Actual content lives inside a <div style="font-family:…"> wrapper
    // that MJML emits for every mj-text.  Fall back to raw td innerHTML if
    // the wrapper is absent (e.g. mj-image blocks).
    const contentDiv = td.querySelector(
      'div[style*="font-family"]',
    ) as HTMLElement | null;
    const content = (contentDiv ? contentDiv.innerHTML : td.innerHTML).trim();
    if (!content) continue;

    const tdAlign  = td.getAttribute('align') || '';
    const tdPad    = getStyleProp(td, 'padding') || '10px 25px';
    // MJML compiler emits `background:` shorthand (not `background-color:`) on the
    // compiled <td> — getStyleProp('background-color') would miss it, so use regex.
    const tdStyleRaw = td.getAttribute('style') || '';
    const tdBgMatch  = tdStyleRaw.match(/\bbackground(?:-color)?\s*:\s*([^;]+)/i);
    const tdBg       = tdBgMatch ? tdBgMatch[1].trim() : '';
    const alignAttr        = tdAlign ? ` align="${tdAlign}"` : '';
    const containerBgAttr  = tdBg   ? ` container-background-color="${tdBg}"` : '';
    // Font size/weight/colour sit on the wrapper div; dropping them reset text to 13px black.
    const textAttrs = contentDiv && contentDiv.parentElement === td ? styleAttrs(contentDiv, TEXT_STYLE_ATTRS) : '';

    blocks.push(`    <mj-text${alignAttr}${containerBgAttr}${textAttrs} padding="${tdPad}">${content}</mj-text>`);
  }
  return blocks;
}

const TEXT_STYLE_ATTRS = [
  'font-family', 'font-size', 'font-weight', 'font-style', 'line-height',
  'letter-spacing', 'color', 'text-decoration', 'text-transform',
];

const CARD_ROW_STYLE_KEYS = [
  'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left', 'background-color',
];

const CARD_STYLE_KEYS = [
  'background-color', 'border', 'border-top', 'border-right', 'border-bottom',
  'border-left', 'border-radius', 'padding',
];

/**
 * Compiled card column: one styled cell holding a table of text rows.
 * Returned as a single <mj-text> holding a one-cell card table + every row (row padding and text style kept).
 */
function columnCard(tbody: HTMLTableSectionElement): string | null {
  if (tbody.rows.length !== 1) return null;
  const td = tbody.rows[0].cells[0];
  if (!td || td.querySelector(':scope > div[style*="font-family"]')) return null;
  const rows = td.querySelector(':scope > table > tbody') as HTMLTableSectionElement | null;
  if (!rows) return null;
  const has_text_row = Array.from(rows.rows).some(
    (r) => !!r.cells[0]?.querySelector(':scope > div[style*="font-family"]'),
  );
  if (!has_text_row) return null;

  const parts: string[] = [];
  for (const tr of Array.from(rows.rows)) {
    const cell = tr.cells[0];
    if (!cell) continue;
    const text = cell.querySelector(':scope > div[style*="font-family"]') as HTMLElement | null;
    const inner = (text ? text.outerHTML : cell.innerHTML).trim();
    if (!inner) continue;
    // Keep the row's own spacing (incl. padding-bottom etc.), background and alignment.
    const row_style = styleCss(cell, CARD_ROW_STYLE_KEYS);
    const align = cell.getAttribute('align');
    parts.push(`<tr><td${align ? ` align="${align}"` : ''}${row_style ? ` style="${row_style}"` : ''}>${inner}</td></tr>`);
  }
  // Tables, not divs: Outlook desktop ignores padding/rounded corners on a <div>.
  const card_style = styleCss(td, CARD_STYLE_KEYS);
  const table = '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"';
  return `    <mj-text padding="0px">${table} style="border-collapse:separate;"><tbody><tr>`
    + `<td style="${card_style}">${table}><tbody>${parts.join('')}</tbody></table></td>`
    + `</tr></tbody></table></mj-text>`;
}

// Inline style → MJML attributes (only the listed properties).
function styleAttrs(el: Element, keys: string[]): string {
  const styles = pickStyles(el, keys);
  return Object.entries(styles).map(([k, v]) => ` ${k}="${v.replace(/"/g, '&quot;')}"`).join('');
}

// Inline style → CSS text (only the listed properties).
function styleCss(el: Element, keys: string[]): string {
  const styles = pickStyles(el, keys);
  return Object.entries(styles).map(([k, v]) => `${k}:${v.replace(/"/g, "'")}`).join(';');
}

function pickStyles(el: Element, keys: string[]): Record<string, string> {
  const styles = parseStyles(el.getAttribute('style') || '');
  if (!styles['background-color'] && styles['background']) styles['background-color'] = styles['background'];
  const out: Record<string, string> = {};
  for (const k of keys) if (styles[k]) out[k] = styles[k];
  return out;
}

// ─── Block extraction (PATH A only) ─────────────────────────────────────────

/** Break a section <td> into individual <mj-text> blocks. */
function extractBlocks(td: HTMLTableCellElement, sectionAlign?: string): string[] {
  const alignAttr = sectionAlign ? ` align="${sectionAlign}"` : '';

  // Common pattern: <td> contains a single layout <table width="100%">
  // whose rows are separate content blocks (greeting, paragraph, card, button…)
  const children = Array.from(td.children);
  const layoutTable =
    children.length === 1 && children[0] instanceof HTMLTableElement
      ? (children[0] as HTMLTableElement)
      : null;

  // {{#each}}/{{/each}} as loose text around the table: splitting by rows would drop them, keep the cell whole.
  const looseBlockToken = Array.from(td.childNodes).some(
    (n) => n.nodeType === 3 && BLOCK_TOKEN.test(n.textContent || ''),
  );
  if (looseBlockToken) {
    const content = td.innerHTML.trim();
    return content ? [`    <mj-text${alignAttr}>${content}</mj-text>`] : [];
  }

  if (layoutTable && hasRowBlockTokens(layoutTable)) {
    return groupRowsByBlockTokens(layoutTable, alignAttr);
  }

  if (layoutTable) {
    const blocks: string[] = [];
    for (const innerRow of Array.from(layoutTable.rows)) {
      const cell = innerRow.cells[0];
      if (!cell) continue;
      const content = cell.innerHTML.trim();
      if (!content) continue;
      blocks.push(`    <mj-text${alignAttr}>${preserveCellStyles(cell, content)}</mj-text>`);
    }
    return blocks;
  }

  // No layout table — return entire td content as one block
  const content = td.innerHTML.trim();
  return content ? [`    <mj-text${alignAttr}>${content}</mj-text>`] : [];
}

// Handlebars block markers: {{#each}}, {{/each}}, {{#if}}, {{/if}}, {{else}}.
const BLOCK_TOKEN = /\{\{\{?\s*(?:[#/]|else\b)/;

/** Direct row-level nodes of a table in document order: protected-token comments and <tr>s. */
function rowLevelNodes(table: HTMLTableElement): Node[] {
  const out: Node[] = [];
  for (const n of Array.from(table.childNodes)) {
    if (n instanceof Element && /^(TBODY|THEAD|TFOOT)$/.test(n.tagName)) out.push(...Array.from(n.childNodes));
    else out.push(n);
  }
  return out.filter((n) => n.nodeType === 8 || (n instanceof Element && n.tagName === 'TR'));
}

function rowToken(n: Node): string | null {
  if (n.nodeType !== 8) return null;
  const data = (n as Comment).data;
  if (!data.startsWith(TABLE_TOKEN_PREFIX)) return null;
  try { return decodeURIComponent(data.slice(TABLE_TOKEN_PREFIX.length)); } catch { return null; }
}

function hasRowBlockTokens(table: HTMLTableElement): boolean {
  return rowLevelNodes(table).some((n) => BLOCK_TOKEN.test(rowToken(n) || ''));
}

/** Rows wrapped in {{#each}}…{{/each}} (or #if) become ONE block with the markers kept, so the loop survives. */
function groupRowsByBlockTokens(table: HTMLTableElement, alignAttr: string): string[] {
  const blocks: string[] = [];
  let group: string[] = [];
  let depth = 0;
  const flush = () => { if (group.length) blocks.push(`    <mj-text${alignAttr}>${group.join('')}</mj-text>`); group = []; };

  for (const n of rowLevelNodes(table)) {
    if (n.nodeType === 8) {
      const token = rowToken(n);
      if (!token) continue;
      if (/^\{\{\{?\s*#/.test(token)) { depth++; group.push(token); }
      else if (/^\{\{\{?\s*\//.test(token)) { if (depth === 0) continue; group.push(token); if (--depth === 0) flush(); }
      else if (depth > 0) group.push(token);
      continue;
    }
    const cell = (n as HTMLTableRowElement).cells[0];
    const content = cell ? cell.innerHTML.trim() : '';
    if (!cell || !content) continue;
    const html = preserveCellStyles(cell, content);
    if (depth > 0) group.push(html);
    else blocks.push(`    <mj-text${alignAttr}>${html}</mj-text>`);
  }
  flush();
  return blocks;
}

/**
 * If a cell has visual styles (background, border), wrap content in a <div>
 * so they're preserved inside <mj-text>.  Padding-only cells are left unwrapped.
 */
function preserveCellStyles(cell: HTMLTableCellElement, content: string): string {
  const raw = cell.getAttribute('style') || '';
  if (!raw) return content;

  const hasVisual = /background-color|border(?!-collapse)/i.test(raw);
  if (!hasVisual) return content;

  const styles = parseStyles(raw);
  const keep = Object.entries(styles)
    .filter(([k]) =>
      k.startsWith('background') || k.startsWith('border') ||
      k === 'padding' || k === 'border-radius' || k === 'margin',
    )
    .map(([k, v]) => `${k}:${v}`)
    .join(';');

  return `<div style="${keep}">${content}</div>`;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getStyleProp(el: Element | null, prop: string): string {
  if (!el) return '';
  const style = el.getAttribute('style') || '';
  const m = style.match(new RegExp(`${prop}\\s*:\\s*([^;]+)`, 'i'));
  return m ? m[1].trim() : '';
}

function parseStyles(style: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const pair of style.split(';')) {
    const i = pair.indexOf(':');
    if (i > 0) out[pair.substring(0, i).trim().toLowerCase()] = pair.substring(i + 1).trim();
  }
  return out;
}

function mjSection(
  opts: { bg?: string; padding?: string; radius?: string },
  blocks: string[],
): string {
  let attrs = ` padding="${opts.padding || '20px 30px'}"`;
  if (opts.bg) attrs += ` background-color="${opts.bg}"`;
  if (opts.radius) attrs += ` border-radius="${opts.radius}"`;
  return `  <mj-section${attrs}>\n    <mj-column>\n${blocks.join('\n')}\n    </mj-column>\n  </mj-section>`;
}

function wrapMjml(bodyBg: string, content: string): string {
  return `<mjml>
<mj-head>
  <mj-attributes>
    <mj-all font-family="Arial, Helvetica, sans-serif" />
    <mj-text font-size="16px" line-height="1.6" color="#555555" />
  </mj-attributes>
</mj-head>
<mj-body background-color="${bodyBg}">
${content}
</mj-body>
</mjml>`;
}
